import { injectable, inject } from 'inversify';
import User from '../entities/User';
import { computed, reactive, ref, Ref, toRefs, watch } from 'vue';
import _ from 'lodash';
import { AccessRights, CRFServiceInterface, NamedBindings, Version, UserServiceInterface, AccessRightsStorageInterface } from '@dandelion/core';
import { Users } from "../enum/Users";
import { UserProvider } from './UserProvider';
import { NamedStorages } from '../enum/NamedStorages';

@injectable()
export class UserService implements UserServiceInterface {
    protected tokenRef = ref(window.localStorage.getItem("jwtToken"));
    protected tokenIsPresent = computed(() => Boolean(this.tokenRef.value));
    protected tokenCheckInterval = 60000;
    protected tokenCheckIntervalId: ReturnType<typeof setInterval> | undefined;
    protected isRemoteAuthEnabled = import.meta.env.VITE_APP_REMOTE_AUTH;

    constructor(
        @inject(NamedBindings.CRFService) protected crfService: CRFServiceInterface,
        @inject(NamedStorages.UserProvider) protected userProvider: UserProvider,
        @inject(NamedBindings.AccessRightsStorage) protected accessRightsStorage: AccessRightsStorageInterface,
        userArray: Array<User> | undefined = undefined,
        protected accessRights: Map<string, AccessRights> = new Map(),
        protected isLogged: Ref<boolean> = ref(false)
    ) {
        if (userArray) {
            this.userArray = userArray;
        }

        if (this.isRemoteAuthEnabled == 'true') {
            isLogged = this.getIsLogged();
            watch(isLogged, (newVal: boolean, oldVal: boolean) => {
                if (newVal === false && oldVal !== false) this.logOut();
            });
            watch(this.tokenIsPresent, (newVal: boolean) => {
                if (!newVal) userProvider.goToLogIn();
            });
            this.checkTokenPeriodically();
        };
    }

    public checkTokenPeriodically() {
        if (this.tokenCheckIntervalId) {
            clearInterval(this.tokenCheckIntervalId);
        }
        this.tokenCheckIntervalId = setInterval(() => {
            const currentToken = window.localStorage.getItem("jwtToken");
            if (this.tokenRef.value !== currentToken) {
                this.tokenRef.value = currentToken;
            }
        }, this.tokenCheckInterval);
    }

    public goToLogIn() {
        this.userProvider.goToLogIn();
    }

    public async logOut() {
        this.setIsLogged(false);
        this.userProvider.logOut();
    }

    readonly userArray: Array<User> = [
        new User(Users.ADMIN, "Ospedale.bergamo", Users.ADMIN),
        new User(Users.WRITER, "Ospedale.bergamo", Users.WRITER),
        new User(Users.READER, "Ospedale.bergamo", Users.READER),
        new User(Users.SUPPORT_ADMIN, "Ospedale.bergamo", Users.SUPPORT_ADMIN),
    ];

    protected state: { currentUser: User | null } = reactive({
        currentUser: import.meta.env.VITE_APP_REMOTE_AUTH == 'true' ? null : this.userArray[0],
    });

    public getIsLogged() {
        if (!import.meta.env.VITE_APP_REMOTE_AUTH || import.meta.env.VITE_APP_REMOTE_AUTH == 'false') {
            this.isLogged.value = true;
        } else {

            const token = window.localStorage.getItem('jwtToken');
            if (token) {
                const tokenData = this.userProvider.verifyToken(token);
                this.isLogged.value = tokenData.validLog;
                this.state.currentUser = tokenData.currentUser;
            }
        }
        return this.isLogged;
    }

    public setIsLogged(boolValue: boolean) {
        this.isLogged.value = boolValue;
    }

    public getCurrentUser() {
        const { currentUser } = toRefs(this.state);
        return currentUser;
    }

    public setCurrentUser(username: string) {
        this.state.currentUser =
            this.userArray.find((el) => el.username == username) ?? new User('', '', '');
    }

    public getCurrentRoles(): Array<string> {
        const u = this.getCurrentUser().value;
        return u ? [u.role] : [];
    }

    async initializeAccessRights(): Promise<void> {
        this.accessRights = await this.accessRightsStorage.getAccessRights();
    }

    getAccessLevelsRights(): Map<string, AccessRights> {
        return this.accessRights;
    }

    getAccessRights(): Map<string, AccessRights> {
        return this.accessRights;
    }

    canRead(variable: string, crfName: string, crfVersion: Version): boolean {
        const chosenLevel = this.searchMostSpecificVariablePrefix(variable, crfName, crfVersion);
        // if (chosenLevel === 'public') {
        //     return true;
        // }
        return this.getAccessLevelsRights().get(chosenLevel)!.read_roles.includes(this.getCurrentRoles()[0]);
    }

    canWrite(variable: string, crfName: string, crfVersion: Version): boolean {
        const chosenLevel = this.searchMostSpecificVariablePrefix(variable, crfName, crfVersion);
        // if (chosenLevel === 'public') {
        //     return true;
        // }
        return this.getAccessLevelsRights().get(chosenLevel)!.write_roles.includes(this.getCurrentRoles()[0]);
    }

    searchMostSpecificVariablePrefix(fullname: string, crfName: string, crfVersion: Version) {
        let chosenAccessLevel = 'public';
        const accessLevels = this.crfService.getAccessLevelsForCRF(crfName, crfVersion);
        const cleanedVariable = fullname.replace(/\[[^\]]+\]/g, '');
        const splittedCleanedVariable = cleanedVariable.split('.');

        function dfs(currentAccessLevelTree: any, level = 0) {
            const children = Object.getOwnPropertyNames(currentAccessLevelTree);
            // if there is a child "accessLevel"
            if (children.includes('accessLevel')) {
                chosenAccessLevel = currentAccessLevelTree.accessLevel;
            }
            let currentChild = splittedCleanedVariable[level];
            // if there is a child with the variable, next step
            if (children.includes(currentChild)) {
                dfs(currentAccessLevelTree[currentChild], level + 1);
            }
        }
        dfs(accessLevels);
        return chosenAccessLevel;
    }

    hasReadAccessRights(accessLevel: string) {
        if (accessLevel == 'public') {
            return true;
        }
        const myRoles = this.getCurrentRoles();
        const rolesThatCanRead = this.getAccessLevelsRights().get(accessLevel)?.read_roles;
        return _.intersection(myRoles, rolesThatCanRead).length > 0;
    }

    hasWriteAccessRights(accessLevel: string) {
        if (accessLevel == 'public') {
            return true;
        }
        const myRoles = this.getCurrentRoles();
        const rolesThatCanWrite = this.getAccessLevelsRights().get(accessLevel)?.write_roles;
        return _.intersection(myRoles, rolesThatCanWrite).length > 0;
    }

    //TODO: remove when auth will be only remote for remote backend
    getHeaders(): any {
        if (import.meta.env.VITE_APP_REMOTE_AUTH == 'true') {
            return {
                "Authorization": `Bearer ${window.localStorage.getItem('jwtToken')}`
            };
        }
        if (!this.getCurrentUser()) {
            return {};
        }
    }
}